import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'

const STAGES = [
  { id: 'all', label: 'All' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'sent', label: 'Sent' },
  { id: 'opened', label: 'Opened' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'submitted', label: 'Submitted' },
  { id: 'in_review', label: 'In review' },
  { id: 'accepted', label: 'Accepted' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'cancelled', label: 'Cancelled' },
]

const STATUS_LABEL = Object.fromEntries(STAGES.filter((s) => s.id !== 'all').map((s) => [s.id, s.label]))

function fmtWhen(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return String(iso)
  }
}

function statusTone(status) {
  if (status === 'accepted') return 'border-teal-200 bg-teal-50 text-teal-800'
  if (status === 'in_review') return 'border-violet-200 bg-violet-50 text-violet-800'
  if (status === 'rejected') return 'border-red-200 bg-red-50 text-red-800'
  if (status === 'overdue') return 'border-red-200 bg-red-50 text-red-700'
  if (status === 'cancelled') return 'border-slate-200 bg-slate-100 text-slate-600'
  if (status === 'submitted') return 'border-blue-200 bg-blue-50 text-blue-800'
  if (['sent', 'opened', 'in_progress'].includes(status)) return 'border-amber-200 bg-amber-50 text-amber-900'
  return 'border-slate-200 bg-slate-50 text-slate-700'
}

export default function FormsPipeline() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [facing, setFacing] = useState('')
  const [q, setQ] = useState('')
  const stage = searchParams.get('stage') || 'all'

  const setStage = (id) => {
    const next = new URLSearchParams(searchParams)
    if (id === 'all') next.delete('stage')
    else next.set('stage', id)
    setSearchParams(next, { replace: true })
  }

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const qs = facing ? `?facing=${encodeURIComponent(facing)}` : ''
      const res = await authFetch(getLocalApiUrl(`/api/forms/assignments${qs}`))
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load pipeline')
      setRows(j.data || [])
    } catch (e) {
      setError(e.message)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [facing])

  useEffect(() => { load() }, [load])

  const counts = useMemo(() => {
    const map = { all: rows.length }
    for (const s of STAGES) {
      if (s.id === 'all') continue
      map[s.id] = rows.filter((r) => r.status === s.id).length
    }
    return map
  }, [rows])

  const filtered = useMemo(() => {
    let list = rows
    if (stage !== 'all') list = list.filter((r) => r.status === stage)
    const term = q.trim().toLowerCase()
    if (!term) return list
    return list.filter((r) => {
      const hay = [
        r.formName,
        r.subjectLabel,
        r.subjectType,
        r.submissionRef,
        r.assigneeUserName,
        r.assigneeRoleLabel,
        r.status,
      ].filter(Boolean).join(' ').toLowerCase()
      return hay.includes(term)
    })
  }, [rows, stage, q])

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Forms pipeline</h1>
          <p className="text-sm text-slate-500 mt-1">
            In-flight form assignments and responses. Use the tabs to filter by stage.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/compliance-safe/forms')}>Forms</Button>
          <Button variant="outline" onClick={() => navigate('/compliance-safe/forms/records')}>Form records</Button>
          <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white" value={facing} onChange={(e) => setFacing(e.target.value)}>
            <option value="">All facings</option>
            <option value="internal">Internal</option>
            <option value="patron">Patron</option>
          </select>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto pb-1 mb-4 border-b border-slate-200">
        {STAGES.map((s) => {
          const active = stage === s.id
          const count = counts[s.id] ?? 0
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setStage(s.id)}
              className={`shrink-0 rounded-t-lg border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${
                active
                  ? 'border-teal-600 text-teal-800 bg-teal-50/60'
                  : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              {s.label}
              <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[11px] ${active ? 'bg-teal-100 text-teal-900' : 'bg-slate-100 text-slate-600'}`}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="block flex-1 min-w-[220px]">
          <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Search</span>
          <input
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="Form, subject, ref, assignee…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <div className="text-sm text-slate-500 pb-2">
          {filtered.length} item{filtered.length === 1 ? '' : 's'}
          {stage !== 'all' ? ` in ${STATUS_LABEL[stage] || stage}` : ''}
        </div>
      </div>

      {error ? <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
      {loading ? <div className="text-sm text-slate-500">Loading…</div> : null}

      {!loading && !filtered.length ? (
        <div className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-200 px-4 py-10 text-center">
          {rows.length ? 'No assignments match this filter.' : 'No assignments yet.'}
        </div>
      ) : null}

      {!loading && filtered.length ? (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <p className="px-4 pt-3 text-xs text-slate-500 sm:hidden">Scroll sideways to see all columns.</p>
          <table className="min-w-[960px] w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-400">
                <th className="px-4 py-2.5 font-semibold">Form</th>
                <th className="px-4 py-2.5 font-semibold">Subject</th>
                <th className="px-4 py-2.5 font-semibold">Facing</th>
                <th className="px-4 py-2.5 font-semibold">Assignee</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Ref</th>
                <th className="px-4 py-2.5 font-semibold">Updated</th>
                <th className="px-4 py-2.5 font-semibold">Due</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr
                  key={a.id}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/compliance-safe/forms/assignments/${a.id}`)}
                >
                  <td className="px-4 py-2.5 font-semibold text-slate-900">
                    {a.formName}
                    {a.isTest ? (
                      <span className="ml-1.5 text-[10px] font-semibold uppercase text-amber-700">Test</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5 text-slate-800 max-w-[200px] truncate">{a.subjectLabel || a.subjectType || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600 capitalize">{a.facing || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600 max-w-[160px] truncate">
                    {a.assigneeUserName || a.assigneeRoleLabel || (a.facing === 'patron' ? 'Patron' : '—')}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${statusTone(a.status)}`}>
                      {STATUS_LABEL[a.status] || a.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{a.submissionRef || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmtWhen(a.updatedAt)}</td>
                  <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmtWhen(a.dueAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  )
}
